import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import config from './config';

interface FAQItem {
  id: number;
  infoName: string;
  infoHeader: string;
  inforDetails: string;
  infoType: string;
  tags: string;
  mainimage: string;
  createdDate: string;
}

type SiteContentResponse = {
  id: number;
  contentType: string;
  title: string;
  summary?: string | null;
  htmlContent: string;
  tags?: string | null;
  mainImageUrl?: string | null;
  publishedAt?: string | null;
  createdAt: string;
};

const mapSiteContentToFaq = (item: SiteContentResponse): FAQItem => ({
  id: item.id,
  infoName: item.title,
  infoHeader: item.summary ?? '',
  inforDetails: item.htmlContent,
  infoType: item.contentType,
  tags: item.tags ?? '',
  mainimage: item.mainImageUrl ?? '',
  createdDate: item.publishedAt || item.createdAt,
});

const FAQ: React.FC = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [faqs, setFaqs] = useState<FAQItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCategory] = useState<string>('All');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedFaq, setSelectedFaq] = useState<FAQItem | null>(null);

  useEffect(() => {
    fetchFAQs();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    // On small screens, start with the list visible and only show details after a user selects an article.
    // This also prevents "Back" from immediately re-selecting the first article.
    if (isMobile) {
      return;
    }

    if (!selectedFaq && faqs.length > 0) {
      setSelectedFaq(faqs[0]);
    }
  }, [faqs, selectedFaq, isMobile]);

  useEffect(() => {
    // Handle browser back button on mobile
    const handlePopState = (event: PopStateEvent) => {
      if (selectedFaq && isMobile) {
        // Prevent default browser behavior and go back to FAQ list instead
        event.preventDefault();
        goBackToGrid();
        // Push a new state to keep browser history in sync
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Push initial state when FAQ details are shown on mobile
    if (selectedFaq && isMobile) {
      window.history.pushState({ faqDetailsOpen: true }, '', window.location.pathname);
    }

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [selectedFaq, isMobile]);

  const fetchFAQs = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${config.apiBaseUrl}/api/sitecontent?contentType=faq`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error(`FAQ API responded with ${response.status}`);
      }

      const raw = await response.json();
      const items = Array.isArray(raw) ? raw as SiteContentResponse[] : [];
      setFaqs(items.map(mapSiteContentToFaq));
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Fetch error:', err);
      setFaqs([]);
    } finally {
      setLoading(false);
    }
  };

  // Filter FAQs based on category and search term
  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'All' || faq.infoType === selectedCategory;
    const matchesSearch = faq.infoName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.infoHeader.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         faq.inforDetails.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Strip HTML tags from content
  const stripHtml = (html: string) => {
    const temp = document.createElement('div');
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || '';
  };

  // Truncate text
  const truncateText = (text: string, maxLength: number = 200) => {
    const stripped = stripHtml(text);
    return stripped.length > maxLength ? stripped.substring(0, maxLength) + '...' : stripped;
  };

  // Handle card click to show full details
  const handleCardClick = (faq: FAQItem) => {
    setSelectedFaq(faq);
  };

  // Close details view and go back to grid
  const goBackToGrid = () => {
    setSelectedFaq(null);
  };

  if (loading) {
    return (
      <div
        className="bg-gradient-to-br from-app-page-from to-app-page-to"
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="app-breadcrumb-bar">
          <div className="app-breadcrumb-row">
            <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
            <span className="app-breadcrumb-sep">›</span>
            <span className="app-breadcrumb-current">Faq</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 pb-4 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-4 md:p-6 bg-gradient-to-r from-blue-500 to-blue-600 text-white">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center text-lg shrink-0">❓</div>
                <div className="min-w-0 text-left">
                  <h1 className="text-xl md:text-2xl font-bold leading-snug">Knowledge Base</h1>
                  <p className="text-app-header-muted text-xs md:text-sm mt-0.5">Find answers and guidance</p>
                </div>
              </div>
            </div>

            <div className="p-6 md:p-8">
              <div className="flex items-center justify-center min-h-[240px]">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-600">Loading...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && faqs.length === 0) {
    return (
      <div
        className="bg-gradient-to-br from-app-page-from to-app-page-to"
        style={{
          width: '100%',
          height: '100vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div className="app-breadcrumb-bar">
          <div className="app-breadcrumb-row">
            <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
            <span className="app-breadcrumb-sep">›</span>
            <span className="app-breadcrumb-current">Faq</span>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-3 pb-4 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
          <div className="mb-3 md:mb-4 bg-red-50 border border-red-200 rounded-lg p-3 md:p-4">
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-gradient-to-br from-app-page-from to-app-page-to"
      style={{
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="app-breadcrumb-bar">
        <div className="app-breadcrumb-row">
          <button onClick={() => navigate('/')} className="app-breadcrumb-link">Home</button>
          <span className="app-breadcrumb-sep">›</span>
          <span className="app-breadcrumb-current">Faq</span>
        </div>
      </div>

      <div className="flex-1 overflow-hidden p-3 pb-4 md:p-4 md:pb-4 lg:p-6 lg:pb-6">
        {error && faqs.length > 0 && (
          <div className="mb-3 md:mb-4 bg-yellow-50 border border-yellow-200 rounded-lg p-3 md:p-4">
            <p className="text-yellow-700 text-sm">{error}</p>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
          <div className="flex-1 min-h-0 p-4 md:p-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6 h-full min-h-0">
              {/* Left: Articles list */}
              <div className={`${selectedFaq ? 'hidden md:block' : 'block'} md:col-span-5 lg:col-span-4 min-h-0`}
              >
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
                  <div className="px-3 py-2.5 md:px-4 md:py-3 border-b border-gray-100 bg-gray-50">
                    <div className="flex items-start gap-3 mb-2">
                      <div className="w-9 h-9 rounded-lg bg-indigo-100 flex items-center justify-center text-lg shrink-0">
                        ❓
                      </div>
                      <div className="min-w-0 text-left">
                        <p className="text-sm font-bold text-gray-900 leading-tight">FAQ</p>
                        <p className="text-xs font-semibold text-gray-500 mt-0.5">
                          {filteredFAQs.length} article{filteredFAQs.length !== 1 ? 's' : ''} available
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 md:p-4 border-b border-gray-100">
                    <div className="relative w-full">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        placeholder="Search articles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full rounded-lg border border-gray-200 bg-white pl-10 pr-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                      />
                    </div>
                  </div>

                  <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-gray-100">
                    {filteredFAQs.map((faq) => {
                      const isSelected = selectedFaq?.id === faq.id;
                      return (
                        <button
                          key={faq.id}
                          type="button"
                          onClick={() => handleCardClick(faq)}
                          className={`w-full text-left p-3 md:p-4 transition-colors ${isSelected ? 'bg-indigo-50' : 'bg-white hover:bg-gray-50'}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <p className={`text-sm font-semibold truncate ${isSelected ? 'text-indigo-900' : 'text-gray-900'}`}>{faq.infoName}</p>
                              <p className="mt-0.5 text-xs text-gray-600 truncate">{faq.infoHeader}</p>
                              <p className="mt-1 text-xs text-gray-500 truncate">{truncateText(faq.inforDetails, 90)}</p>
                            </div>
                            <span className="shrink-0 text-gray-300 font-bold">›</span>
                          </div>
                        </button>
                      );
                    })}

                    {filteredFAQs.length === 0 && (
                      <div className="p-6 text-left">
                        <div className="text-3xl mb-3">🔍</div>
                        <p className="text-sm font-semibold text-gray-800">No articles found</p>
                        <p className="text-xs text-gray-500 mt-1">Try adjusting your search term.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right: Details */}
              <div className={`${!selectedFaq ? 'hidden md:block' : 'block'} md:col-span-7 lg:col-span-8 min-h-0`}
              >
                <div className="bg-white rounded-lg shadow-md overflow-hidden h-full flex flex-col min-h-0">
                  <div className="px-4 py-2.5 md:px-6 md:py-3 border-b border-white/15 bg-gradient-to-r from-blue-500 to-blue-600 text-white flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 min-w-0">
                      {selectedFaq && (
                        <button
                          type="button"
                          onClick={goBackToGrid}
                          className="md:hidden inline-flex items-center justify-center rounded-lg border border-white/30 bg-white/10 px-2.5 py-2 text-white hover:bg-white/15"
                          aria-label="Back"
                          title="Back"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 20 20" stroke="currentColor" strokeWidth="2">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 6l-5 4 5 4" />
                          </svg>
                        </button>
                      )}

                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center text-base shrink-0">❓</div>
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-semibold leading-tight truncate">Knowledge Base</p>
                          <p className="text-xs leading-tight text-white/80 truncate">Find answers and guidance</p>
                        </div>
                      </div>
                    </div>

                    {selectedFaq && (
                      <span className="shrink-0 text-xs text-white/85">
                        {new Date(selectedFaq.createdDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                      </span>
                    )}
                  </div>

                  {selectedFaq ? (
                    <>
                      <div className="flex-1 min-h-0 overflow-y-auto p-4 md:p-6">
                        <div className="min-h-full flex flex-col">
                          <h2 className="text-lg md:text-xl font-bold text-gray-900 leading-snug text-left">{selectedFaq.infoName}</h2>
                          <p className="mt-2 text-sm text-gray-600 text-left">{selectedFaq.infoHeader}</p>

                          {selectedFaq.mainimage && (
                            <div className="mt-4">
                              <img
                                src={selectedFaq.mainimage}
                                alt={selectedFaq.infoName}
                                className="w-full max-h-[420px] object-cover rounded-xl"
                              />
                            </div>
                          )}

                          <div
                            className="faq-content mt-5 text-left"
                            dangerouslySetInnerHTML={{ __html: selectedFaq.inforDetails }}
                          />

                        <style>{`
                          .faq-content ul {
                            margin: 16px 0 !important;
                            padding-left: 24px !important;
                            list-style-type: disc !important;
                          }
                          
                          .faq-content ul ul {
                            margin: 8px 0 !important;
                            padding-left: 20px !important;
                            list-style-type: circle !important;
                          }
                          
                          .faq-content ol {
                            margin: 16px 0 !important;
                            padding-left: 24px !important;
                            list-style-type: decimal !important;
                          }
                          
                          .faq-content li {
                            margin: 8px 0 !important;
                            padding-left: 4px !important;
                            line-height: 1.6 !important;
                          }
                          
                          .faq-content li p {
                            margin: 4px 0 !important;
                          }

                          .faq-content dl {
                            margin: 20px 0 !important;
                            padding: 16px !important;
                            background-color: #f8f9fa !important;
                            border-radius: 8px !important;
                            border-left: 4px solid #2c5aa0 !important;
                          }

                          .faq-content dt {
                            font-weight: 600 !important;
                            color: #2c5aa0 !important;
                            margin: 12px 0 4px 0 !important;
                            font-size: 1rem !important;
                            display: inline-block !important;
                            min-width: 120px !important;
                          }

                          .faq-content dd {
                            margin: 0 0 12px 0 !important;
                            padding-left: 8px !important;
                            color: #333 !important;
                            font-size: 1rem !important;
                            display: inline-block !important;
                            margin-left: 8px !important;
                          }

                          .faq-content dt:first-of-type {
                            margin-top: 0 !important;
                          }

                          .faq-content dd:last-of-type {
                            margin-bottom: 0 !important;
                          }

                          .faq-content h1, .faq-content h2, .faq-content h3,
                          .faq-content h4, .faq-content h5, .faq-content h6 {
                            margin: 24px 0 16px 0 !important;
                            font-weight: 600 !important;
                            color: #2c5aa0 !important;
                          }

                          .faq-content p {
                            margin: 12px 0 !important;
                            line-height: 1.7 !important;
                            color: #374151 !important;
                          }

                          .faq-content blockquote {
                            margin: 16px 0 !important;
                            padding: 16px 20px !important;
                            border-left: 4px solid #2c5aa0 !important;
                            background-color: #f8f9fa !important;
                            font-style: italic !important;
                          }

                          .faq-content code {
                            background-color: #f1f1f1 !important;
                            padding: 2px 6px !important;
                            border-radius: 4px !important;
                            font-family: 'Courier New', monospace !important;
                            font-size: 0.9em !important;
                          }

                          .faq-content pre {
                            background-color: #f8f9fa !important;
                            padding: 16px !important;
                            border-radius: 8px !important;
                            overflow-x: auto !important;
                            margin: 16px 0 !important;
                          }

                          .faq-content a {
                            color: #2c5aa0 !important;
                            text-decoration: underline !important;
                          }

                          .faq-content a:hover {
                            color: #1e3d72 !important;
                          }

                          .faq-content strong, .faq-content b {
                            font-weight: 600 !important;
                          }

                          .faq-content em, .faq-content i {
                            font-style: italic !important;
                          }

                          .faq-content table {
                            width: 100% !important;
                            border-collapse: collapse !important;
                            margin: 16px 0 !important;
                            background-color: #fff !important;
                            box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
                          }

                          .faq-content th, .faq-content td {
                            padding: 12px !important;
                            text-align: left !important;
                            border-bottom: 1px solid #e0e0e0 !important;
                          }

                          .faq-content th {
                            background-color: #f8f9fa !important;
                            font-weight: 600 !important;
                            color: #2c5aa0 !important;
                          }

                          .faq-content tr:hover {
                            background-color: #f8f9fa !important;
                          }
                        `}</style>

                          <div className="mt-auto pt-6">
                            {selectedFaq.tags && (
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 md:p-4">
                                <p className="text-xs font-semibold text-slate-600 mb-2">Related Topics</p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedFaq.tags.split(',').map((tag, index) => (
                                    <span
                                      key={index}
                                      className="inline-flex items-center rounded-full bg-blue-600 px-3 py-1 text-xs font-semibold text-white"
                                    >
                                      #{tag.trim()}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className={`${selectedFaq.tags ? 'mt-6' : ''} grid grid-cols-3 gap-2 md:gap-3 text-[11px] md:text-xs text-gray-600`}>
                              <div className="rounded-lg border border-blue-200 bg-blue-50 p-2.5 md:p-3 min-w-0">
                                <p className="font-semibold text-blue-900">Published</p>
                                <p className="mt-0.5 font-semibold text-blue-800 leading-tight break-words">{new Date(selectedFaq.createdDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                              </div>
                              <div className="rounded-lg border border-indigo-200 bg-indigo-50 p-2.5 md:p-3 min-w-0">
                                <p className="font-semibold text-indigo-900">Article ID</p>
                                <p className="mt-0.5 font-semibold text-indigo-800 leading-tight break-words">#{selectedFaq.id}</p>
                              </div>
                              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2.5 md:p-3 min-w-0">
                                <p className="font-semibold text-emerald-900">Category</p>
                                <p className="mt-0.5 font-semibold text-emerald-800 leading-tight break-words">{selectedFaq.infoType}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 min-h-0 flex items-center justify-center p-8 text-center">
                      <div>
                        <div className="text-5xl mb-4">📚</div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-1">Pick an article to begin</h3>
                        <p className="text-sm text-gray-500 max-w-sm">Select an item from the list to view details here.</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQ;